/**
 * QuickFilters — filtres rapides sur les résultats de recherche
 * Chips cliquables générés depuis les facettes des résultats
 */

import { Trans, useTranslation } from 'react-i18next';
import type { PublicFiche } from './types';

// Codes solution reconnus — libellés traduits via search.solutions.<code>
const SOLUTION_CODES = new Set([
  'cassation', 'rejet', 'irrecevabilite', 'nonlieu', 'annulation', 'avis', 'renvoi',
  'rabat', 'decheance', 'infirmation', 'confirmation', 'infirmation_partielle',
  'desistement', 'mainlevee',
]);

// Clés de facette internes (non envoyées à l'API) : 'cc' | 'ca'
const getJurLabel = (f: PublicFiche) =>
  f.jurisdiction === 'cc' || (f.jurisdiction || '').toLowerCase().includes('cassation')
    ? 'cc'
    : 'ca';

const getSolLabel = (f: PublicFiche) => {
  const code = (f.solution || '').toLowerCase();
  return SOLUTION_CODES.has(code) ? code : null;
};

interface QuickFiltersProps {
  results:  PublicFiche[];
  active:   Record<string, string>;
  onChange: (filters: Record<string, string>) => void;
}

export const QuickFilters = ({ results, active, onChange }: QuickFiltersProps) => {
  const { t } = useTranslation();
  if (!results.length) return null;

  // Facettes
  const jurisdictions = new Map<string, number>();
  const solutions     = new Map<string, number>();
  const domaines      = new Map<string, number>();
  let   principeCount = 0;

  for (const f of results) {
    const jur = getJurLabel(f);
    jurisdictions.set(jur, (jurisdictions.get(jur) ?? 0) + 1);

    const sol = getSolLabel(f);
    if (sol) solutions.set(sol, (solutions.get(sol) ?? 0) + 1);

    if (f.domaine) domaines.set(f.domaine, (domaines.get(f.domaine) ?? 0) + 1);

    if (f.is_arret_principe === true) principeCount++;
  }

  const hasActive = Object.keys(active).length > 0;

  const filteredCount = results.filter(f => {
    if (active.jurisdiction    && getJurLabel(f) !== active.jurisdiction) return false;
    if (active.solution        && getSolLabel(f) !== active.solution)     return false;
    if (active.domaine         && f.domaine      !== active.domaine)      return false;
    if (active.is_arret_principe === 'true' && f.is_arret_principe !== true) return false;
    return true;
  }).length;

  const toggle = (key: string, value: string) => {
    if (active[key] === value) {
      const next = { ...active };
      delete next[key];
      onChange(next);
    } else {
      onChange({ ...active, [key]: value });
    }
  };

  const Chip = ({
    filterKey, value, label, count, amber,
  }: {
    filterKey: string;
    value:     string;
    label?:    string;
    count:     number;
    amber?:    boolean;
  }) => {
    const isActive = active[filterKey] === value;
    return (
      <button
        onClick={() => toggle(filterKey, value)}
        className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border transition-all font-medium whitespace-nowrap ${
          isActive
            ? amber
              ? 'bg-amber-500 text-white border-amber-500 shadow-sm'
              : 'bg-primary text-primary-foreground border-primary shadow-sm'
            : amber
              ? 'bg-amber-50 text-amber-800 border-amber-300 hover:bg-amber-100 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-700'
              : 'bg-background text-muted-foreground border-border hover:border-primary/40 hover:text-foreground'
        }`}
      >
        {label ?? value}
        <span className={`text-[10px] px-1 rounded-full ${
          isActive
            ? amber ? 'bg-white/30' : 'bg-white/20'
            : 'bg-muted text-muted-foreground'
        }`}>
          {count}
        </span>
      </button>
    );
  };

  const Divider = () => (
    <span className="text-border/60 text-sm select-none">·</span>
  );

  const showPrincipe = principeCount > 0;

  return (
    <div className="flex flex-col gap-2 py-3 border-b mb-4">
      <div className="flex items-center justify-between min-h-[20px]">
        {hasActive ? (
          <p className="text-xs text-muted-foreground">
            <Trans
              i18nKey="search.quick.filtered"
              values={{ filtered: filteredCount, total: results.length }}
              components={[<span key="0" className="font-semibold text-foreground" />]}
            />
          </p>
        ) : (
          <p className="text-xs text-muted-foreground font-medium">{t('search.quick.filter_by')}</p>
        )}
        {hasActive && (
          <button
            onClick={() => onChange({})}
            className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors"
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            {t('search.quick.clear')}
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        {/* ⭐ Arrêts de principe — en premier si présents */}
        {showPrincipe && (
          <>
            <Chip
              filterKey="is_arret_principe"
              value="true"
              label={t('search.quick.principe')}
              count={principeCount}
              amber
            />
            {(jurisdictions.size > 1 || solutions.size > 0 || domaines.size > 1) && <Divider />}
          </>
        )}

        {/* Juridiction */}
        {jurisdictions.size > 1 && (
          <>
            {Array.from(jurisdictions.entries()).map(([val, n]) => (
              <Chip key={val} filterKey="jurisdiction" value={val} label={t(`search.quick.jur_${val}`)} count={n} />
            ))}
            {(solutions.size > 0 || domaines.size > 1) && <Divider />}
          </>
        )}

        {/* Solutions top 5 */}
        {Array.from(solutions.entries())
          .sort((a, b) => b[1] - a[1])
          .slice(0, 5)
          .map(([val, n]) => (
            <Chip key={val} filterKey="solution" value={val} label={t(`search.solutions.${val}`)} count={n} />
          ))
        }

        {/* Domaines top 4 */}
        {solutions.size > 0 && domaines.size > 1 && <Divider />}
        {Array.from(domaines.entries())
          .sort((a, b) => b[1] - a[1])
          .slice(0, 4)
          .map(([val, n]) => (
            <Chip key={val} filterKey="domaine" value={val} count={n} />
          ))
        }
      </div>
    </div>
  );
};

// Helper exporté pour filtrer les résultats
export const applyQuickFilters = (
  results: PublicFiche[],
  active: Record<string, string>,
): PublicFiche[] => {
  if (!Object.keys(active).length) return results;
  return results.filter(f => {
    if (active.jurisdiction      && getJurLabel(f) !== active.jurisdiction) return false;
    if (active.solution          && getSolLabel(f) !== active.solution)     return false;
    if (active.domaine           && f.domaine      !== active.domaine)      return false;
    if (active.is_arret_principe === 'true' && f.is_arret_principe !== true) return false;
    return true;
  });
};